// The email design system: looks (presets), fonts, palettes and the salon's
// brand kit. Sections never hard-code colours or fonts; they ask for a tone
// ("paper", "tint", "accent", "deep") and get colours from here, so one brand
// colour re-skins a whole email.

// ─── Fonts ───
// Web fonts load in Apple Mail, iOS Mail, Outlook for Mac and most phones.
// Gmail and classic Outlook strip them, so every stack ends in a system font
// with the same feel (serif to Georgia, condensed to Arial Narrow).
export const FONTS = {
  fraunces: { label: 'Fraunces', kind: 'serif', stack: "'Fraunces', Georgia, 'Times New Roman', serif", google: 'Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;1,9..144,400' },
  instrument: { label: 'Instrument Serif', kind: 'serif', stack: "'Instrument Serif', Georgia, 'Times New Roman', serif", google: 'Instrument+Serif:ital@0;1' },
  cormorant: { label: 'Cormorant', kind: 'serif', stack: "'Cormorant Garamond', Garamond, Georgia, serif", google: 'Cormorant+Garamond:ital,wght@0,500;0,600;1,500' },
  dmSerif: { label: 'DM Serif Display', kind: 'serif', stack: "'DM Serif Display', Georgia, 'Times New Roman', serif", google: 'DM+Serif+Display:ital@0;1' },
  georgia: { label: 'Georgia', kind: 'serif', stack: "Georgia, 'Times New Roman', serif", google: null },
  oswald: { label: 'Oswald', kind: 'condensed', stack: "'Oswald', 'Arial Narrow', Arial, sans-serif", google: 'Oswald:wght@500;600;700' },
  anton: { label: 'Anton', kind: 'condensed', stack: "'Anton', Impact, 'Arial Narrow', Arial, sans-serif", google: 'Anton' },
  bricolage: { label: 'Bricolage Grotesque', kind: 'sans', stack: "'Bricolage Grotesque', Arial, Helvetica, sans-serif", google: 'Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700;12..96,800' },
  figtree: { label: 'Figtree', kind: 'sans', stack: "'Figtree', Arial, Helvetica, sans-serif", google: 'Figtree:ital,wght@0,400;0,500;0,600;0,700;1,400' },
  dmSans: { label: 'DM Sans', kind: 'sans', stack: "'DM Sans', Arial, Helvetica, sans-serif", google: 'DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400' },
  manrope: { label: 'Manrope', kind: 'sans', stack: "'Manrope', Arial, Helvetica, sans-serif", google: 'Manrope:wght@400;500;600;700;800' },
  jost: { label: 'Jost', kind: 'sans', stack: "'Jost', 'Century Gothic', Arial, sans-serif", google: 'Jost:ital,wght@0,400;0,500;0,600;1,400' },
  archivoBlack: { label: 'Archivo Black', kind: 'sans', stack: "'Archivo Black', 'Arial Black', Arial, Helvetica, sans-serif", google: 'Archivo+Black' },
  archivo: { label: 'Archivo', kind: 'sans', stack: "'Archivo', Arial, Helvetica, sans-serif", google: 'Archivo:wght@400;500;600;700;800' },
  system: { label: 'System', kind: 'sans', stack: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif", google: null },
}
export const fontStack = (key) => (FONTS[key] || FONTS.system).stack
// What classic Outlook should fall back to (it ignores web fonts and would
// otherwise drop to Times New Roman).
export const fontFallback = (key) => {
  const kind = (FONTS[key] || FONTS.system).kind
  if (kind === 'serif') return "Georgia, 'Times New Roman', serif"
  if (kind === 'condensed') return "'Arial Narrow', Arial, sans-serif"
  return 'Arial, Helvetica, sans-serif'
}
export const googleFontsHref = (keys) => {
  const fams = Array.from(new Set(keys.filter(Boolean))).map(k => FONTS[k] && FONTS[k].google).filter(Boolean)
  if (!fams.length) return null
  return `https://fonts.googleapis.com/css2?${fams.map(f => `family=${f}`).join('&')}&display=swap`
}

// ─── Looks ───
// Each look is a complete design: palette, type, buttons and corners. They're
// grounded in their market's world (colour charts and gloss for hair; enamel
// signs and price boards for barbers; clinic calm, candy and spa for beauty).
export const LOOKS = {
  gloss: {
    id: 'gloss', name: 'Gloss', market: 'hair', blurb: 'Glossy, editorial, a little fashion',
    palette: { canvas: '#EFEAE6', paper: '#FFFFFF', ink: '#1C1718', muted: '#6F6568', line: '#E8E1DD', accent: '#6E1A2E', onAccent: '#FFFFFF', tint: '#F7EEEE', deep: '#1C1718', onDeep: '#F7F1EE' },
    type: { display: 'fraunces', text: 'figtree', displayWeight: 400, displayCase: 'none', displayTracking: '-0.02em', italicAccent: true },
    button: { shape: 'pill', style: 'solid', case: 'upper' }, radius: 0,
  },
  colourbar: {
    id: 'colourbar', name: 'Colour Bar', market: 'hair', blurb: 'Bright, modern, full of colour',
    palette: { canvas: '#FFF4EE', paper: '#FFFFFF', ink: '#1F1B2D', muted: '#625D72', line: '#F1E6E0', accent: '#E4572E', onAccent: '#FFFFFF', tint: '#FFEADF', deep: '#1F1B2D', onDeep: '#FFF4EE' },
    type: { display: 'bricolage', text: 'figtree', displayWeight: 800, displayCase: 'none', displayTracking: '-0.03em', italicAccent: false },
    button: { shape: 'rounded', style: 'solid', case: 'none' }, radius: 16,
  },
  // A modern hair salon: burgundy, terracotta and cream, soft rounded cards,
  // classic serif headlines with italic accents.
  atelier: {
    id: 'atelier', name: 'Atelier', market: 'hair', blurb: 'Burgundy and terracotta, soft cards, serif charm',
    palette: { canvas: '#D4C8BA', paper: '#FFFFFF', ink: '#272320', muted: '#69625D', line: '#E8E0D9', accent: '#9F3328', onAccent: '#FFF3E6', tint: '#F6F2ED', deep: '#491B1E', onDeep: '#FFF7EC' },
    type: { display: 'georgia', text: 'dmSans', displayWeight: 400, displayCase: 'none', displayTracking: '-0.02em', italicAccent: true },
    button: { shape: 'rounded', style: 'solid', case: 'none' }, radius: 16,
  },
  heritage: {
    id: 'heritage', name: 'Heritage', market: 'barber', blurb: 'Old-school shop, price-board type',
    palette: { canvas: '#E3DCCD', paper: '#F7F2E8', ink: '#1C1A17', muted: '#6B6357', line: '#DDD3C2', accent: '#1F4B39', onAccent: '#F7F2E8', tint: '#EDE5D6', deep: '#1C1A17', onDeep: '#F2EBDD' },
    type: { display: 'oswald', text: 'dmSans', displayWeight: 600, displayCase: 'upper', displayTracking: '0.01em', italicAccent: false },
    button: { shape: 'square', style: 'solid', case: 'upper' }, radius: 0,
  },
  fade: {
    id: 'fade', name: 'Fade', market: 'barber', blurb: 'Sharp, black and white, one loud colour',
    palette: { canvas: '#E8E8E6', paper: '#FFFFFF', ink: '#0E0E0E', muted: '#5E5E5B', line: '#E4E4E1', accent: '#D7263D', onAccent: '#FFFFFF', tint: '#F2F2F0', deep: '#0E0E0E', onDeep: '#FFFFFF' },
    type: { display: 'anton', text: 'archivo', displayWeight: 400, displayCase: 'upper', displayTracking: '0.01em', italicAccent: false },
    button: { shape: 'square', style: 'solid', case: 'upper' }, radius: 0,
  },
  // A gig poster for the neighbourhood barber: charcoal pages, giant black
  // capitals in acid yellow, a cobalt panel for the big moments.
  poster: {
    id: 'poster', name: 'Poster', market: 'barber', blurb: 'Giant type, acid and cobalt, gig-poster loud',
    palette: { canvas: '#0F1211', paper: '#171B19', ink: '#DEFA48', muted: '#C0C5B9', line: '#3A403A', accent: '#DEFA48', onAccent: '#171B19', tint: '#222724', deep: '#2445D8', onDeep: '#DEFA48', onDeepMuted: '#F5F4ED', pop: '#DEFA48', onPop: '#171B19' },
    type: { display: 'archivoBlack', text: 'archivo', displayWeight: 400, displayCase: 'upper', displayTracking: '-0.03em', italicAccent: false },
    button: { shape: 'pill', style: 'solid', case: 'none' }, radius: 0,
  },
  clinic: {
    id: 'clinic', name: 'Clinic', market: 'beauty', blurb: 'Calm, clean, clinical skin',
    palette: { canvas: '#ECF1F1', paper: '#FFFFFF', ink: '#17272B', muted: '#5A6A6D', line: '#E0E8E8', accent: '#3F6F76', onAccent: '#FFFFFF', tint: '#E9F1F1', deep: '#17272B', onDeep: '#EEF4F4' },
    type: { display: 'instrument', text: 'manrope', displayWeight: 400, displayCase: 'none', displayTracking: '-0.01em', italicAccent: true },
    button: { shape: 'pill', style: 'solid', case: 'none' }, radius: 16,
  },
  blush: {
    id: 'blush', name: 'Blush', market: 'beauty', blurb: 'Playful pink for nails and lashes',
    palette: { canvas: '#FCEEF2', paper: '#FFFFFF', ink: '#2A1620', muted: '#7A6570', line: '#F4DEE6', accent: '#D63A6E', onAccent: '#FFFFFF', tint: '#FFE9F0', deep: '#2A1620', onDeep: '#FFF0F5' },
    type: { display: 'dmSerif', text: 'dmSans', displayWeight: 400, displayCase: 'none', displayTracking: '-0.01em', italicAccent: true },
    button: { shape: 'pill', style: 'solid', case: 'none' }, radius: 16,
  },
  spa: {
    id: 'spa', name: 'Spa', market: 'beauty', blurb: 'Quiet luxury, soft and slow',
    palette: { canvas: '#ECE5DF', paper: '#FBF8F4', ink: '#2D2421', muted: '#766A64', line: '#E8DED6', accent: '#7B4F4B', onAccent: '#FBF8F4', tint: '#F3EAE3', deep: '#2D2421', onDeep: '#F3EAE3' },
    type: { display: 'cormorant', text: 'jost', displayWeight: 500, displayCase: 'none', displayTracking: '0', italicAccent: true },
    button: { shape: 'pill', style: 'outline', case: 'upper' }, radius: 0,
  },
  // A neighbourhood zine: warm paper, ink, lilac, and an acid "pop" for the
  // loud moments. Heavy grotesque headlines with serif italic accents.
  zine: {
    id: 'zine', name: 'Zine', market: 'all', blurb: 'Loud type, acid and lilac, like a local zine',
    palette: { canvas: '#E6E4DC', paper: '#F7F3E8', ink: '#24231F', muted: '#57544B', line: '#D9D4C5', accent: '#24231F', onAccent: '#F7F3E8', tint: '#D9C9EF', deep: '#24231F', onDeep: '#F7F3E8', pop: '#E6F36A', onPop: '#24231F' },
    type: { display: 'archivo', text: 'archivo', displayWeight: 800, displayCase: 'none', displayTracking: '-0.045em', italicAccent: true, italicFont: 'instrument' },
    button: { shape: 'square', style: 'solid', case: 'none' }, radius: 0,
  },
  letter: {
    id: 'letter', name: 'Letter', market: 'all', blurb: 'Plain and personal, like a note',
    palette: { canvas: '#FFFFFF', paper: '#FFFFFF', ink: '#1F1F1F', muted: '#666666', line: '#E6E6E6', accent: '#29282D', onAccent: '#FFFFFF', tint: '#F5F5F4', deep: '#1F1F1F', onDeep: '#FFFFFF' },
    type: { display: 'system', text: 'system', displayWeight: 700, displayCase: 'none', displayTracking: '-0.01em', italicAccent: false },
    button: { shape: 'rounded', style: 'solid', case: 'none' }, radius: 8,
  },
}
export const LOOK_ORDER = ['gloss', 'colourbar', 'atelier', 'heritage', 'fade', 'poster', 'clinic', 'blush', 'spa', 'zine', 'letter']
export const DEFAULT_LOOK = 'letter'

// ─── Colour maths ───
const clamp = (n, a, b) => Math.min(b, Math.max(a, n))
export const normHex = (hex) => {
  if (typeof hex !== 'string') return null
  let h = hex.trim().replace('#', '')
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  return /^[0-9a-f]{6}$/i.test(h) ? `#${h.toUpperCase()}` : null
}
const toRgb = (hex) => {
  const h = normHex(hex) || '#000000'
  return [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16))
}
const toHex = (rgb) => '#' + rgb.map(v => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('').toUpperCase()
// mix(a, b, t): t=0 gives a, t=1 gives b
export const mix = (a, b, t) => {
  const A = toRgb(a), B = toRgb(b)
  return toHex(A.map((v, i) => v + (B[i] - v) * t))
}
export const luminance = (hex) => {
  const [r, g, b] = toRgb(hex).map(v => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}
export const contrast = (a, b) => {
  const la = luminance(a), lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}
// Nudge `fg` towards black or white until it reads on `bg`.
export const readableOn = (fg, bg, min = 4.5) => {
  if (contrast(fg, bg) >= min) return normHex(fg)
  const target = luminance(bg) > 0.4 ? '#000000' : '#FFFFFF'
  for (let t = 0.1; t <= 1.001; t += 0.1) {
    const c = mix(fg, target, t)
    if (contrast(c, bg) >= min) return c
  }
  return target
}
const bestInk = (bg, dark, light) => (contrast(dark, bg) >= contrast(light, bg) ? dark : light)

// ─── Theme ───
// A theme spec is what an email stores: { look, accent?, display?, text?,
// button?, radius?, logo?, logoWidth?, logoShape?, name? }. resolveTheme turns
// it into concrete colours and fonts.
export const resolveTheme = (spec = {}) => {
  const look = LOOKS[spec.look] || LOOKS[DEFAULT_LOOK]
  const base = look.palette
  const p = { ...base }
  const brand = normHex(spec.accent)
  if (brand && brand !== normHex(base.accent)) {
    p.accent = brand
    // Dark looks (light ink on dark paper) take their dark ink from the paper
    p.onAccent = bestInk(brand, luminance(base.ink) < 0.4 ? base.ink : base.paper, '#FFFFFF')
    // Tint follows the brand colour, very light, over the look's paper
    p.tint = mix(brand, base.paper, 0.9)
  }
  // Colour used for accent TEXT on paper/tint (eyebrows, prices, links):
  // the brand colour where it reads, a deeper shade where it doesn't.
  p.accentText = readableOn(p.accent, p.paper, 3.6)
  const type = {
    ...look.type,
    display: FONTS[spec.display] ? spec.display : look.type.display,
    text: FONTS[spec.text] ? spec.text : look.type.text,
  }
  // A font swap keeps the look's case/weight only when the kinds match
  if (spec.display && FONTS[spec.display] && FONTS[spec.display].kind !== FONTS[look.type.display].kind) {
    const kind = FONTS[spec.display].kind
    type.displayCase = kind === 'condensed' ? 'upper' : 'none'
    type.displayWeight = kind === 'serif' ? 400 : kind === 'condensed' ? 600 : 700
    type.displayTracking = kind === 'condensed' ? '0.01em' : '-0.02em'
  }
  return {
    look: look.id,
    lookName: look.name,
    palette: p,
    type,
    button: { ...look.button, ...(spec.button || {}) },
    radius: typeof spec.radius === 'number' ? spec.radius : look.radius,
    logo: spec.logo || '',
    logoWidth: Number(spec.logoWidth) || 132,
    // 'natural' shows the logo as it is. 'round' and 'square' crop a photo
    // (the salon image many use in Settings > Email branding) to a circle or
    // a rounded square, with the name under it.
    logoShape: spec.logoShape === 'round' || spec.logoShape === 'square' ? spec.logoShape : 'natural',
    name: spec.name || '',
  }
}

// Section tones: every section picks one; text/button colours follow.
export const TONES = [
  { id: 'paper', label: 'Paper' },
  { id: 'tint', label: 'Tint' },
  { id: 'accent', label: 'Brand' },
  { id: 'pop', label: 'Pop' },
  { id: 'deep', label: 'Dark' },
  { id: 'canvas', label: 'Canvas' },
]
// Pop is a second, louder colour only some looks have (Zine's acid); in the
// others a Pop section wears the brand colour instead.
export const tonesFor = (theme) => TONES.filter(t => t.id !== 'pop' || !!(theme && theme.palette && theme.palette.pop))
export const tone = (theme, id) => {
  const p = theme.palette
  switch (id) {
    case 'tint':
      return { id, bg: p.tint, text: p.ink, muted: mix(p.muted, p.ink, 0.15), line: mix(p.line, p.ink, 0.06), accentText: readableOn(p.accent, p.tint, 3.6), btnBg: p.accent, btnText: p.onAccent, card: p.paper }
    case 'accent': {
      // A brand-coloured section carries body text, so the band deepens just
      // enough for it to read at 4.5:1 (coral with white text, for one).
      // Buttons elsewhere keep the exact brand colour.
      let bg = p.accent
      if (contrast(p.onAccent, bg) < 4.5) {
        const away = luminance(p.onAccent) > 0.5 ? '#000000' : '#FFFFFF'
        for (let t = 0.04; t <= 0.8; t += 0.04) {
          const c = mix(p.accent, away, t)
          if (contrast(p.onAccent, c) >= 4.5) { bg = c; break }
        }
      }
      return { id, bg, text: p.onAccent, muted: mix(p.onAccent, bg, 0.22), line: mix(p.onAccent, bg, 0.78), accentText: p.onAccent, btnBg: p.onAccent, btnText: readableOn(bg, p.onAccent, 3), card: mix(bg, p.onAccent, 0.1) }
    }
    case 'pop': {
      if (!p.pop) return { ...tone(theme, 'accent'), id: 'pop' }
      return { id, bg: p.pop, text: p.onPop, muted: mix(p.onPop, p.pop, 0.2), line: mix(p.onPop, p.pop, 0.8), accentText: p.onPop, btnBg: p.onPop, btnText: p.pop, card: mix(p.pop, '#FFFFFF', 0.35) }
    }
    case 'deep': {
      // A look with a pop colour uses it on dark: acid buttons on ink
      if (p.pop && contrast(p.pop, p.deep) >= 3) {
        return { id, bg: p.deep, text: p.onDeep, muted: p.onDeepMuted || mix(p.onDeep, p.deep, 0.3), line: mix(p.onDeep, p.deep, 0.84), accentText: p.pop, btnBg: p.pop, btnText: p.onPop, card: mix(p.deep, p.onDeep, 0.08) }
      }
      const accentOk = contrast(p.accent, p.deep) >= 2.4
      return { id, bg: p.deep, text: p.onDeep, muted: p.onDeepMuted || mix(p.onDeep, p.deep, 0.35), line: mix(p.onDeep, p.deep, 0.84), accentText: accentOk ? readableOn(p.accent, p.deep, 3) : p.onDeep, btnBg: accentOk ? p.accent : p.onDeep, btnText: accentOk ? p.onAccent : p.deep, card: mix(p.deep, p.onDeep, 0.08) }
    }
    case 'canvas':
      return { id, bg: p.canvas, text: p.ink, muted: p.muted, line: mix(p.line, p.ink, 0.06), accentText: readableOn(p.accent, p.canvas, 3.6), btnBg: p.accent, btnText: p.onAccent, card: p.paper }
    case 'paper':
    default:
      return { id: 'paper', bg: p.paper, text: p.ink, muted: p.muted, line: p.line, accentText: p.accentText, btnBg: p.accent, btnText: p.onAccent, card: p.tint }
  }
}

// ─── The salon's brand kit ───
// Seeded from Settings > Email branding (brand colour, salon image, name) and
// saved per salon once edited here. It's a theme spec without a look:
// { accent, logo, logoWidth, name, display?, text?, button?, radius? }.
const kitKey = (salonId) => `beu-email-brand-${salonId || 'default'}`
export const loadBrandKit = (salonId) => {
  try {
    const raw = localStorage.getItem(kitKey(salonId))
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}
export const saveBrandKit = (salonId, kit) => {
  try { localStorage.setItem(kitKey(salonId), JSON.stringify(kit)) } catch (e) { /* private mode: lasts the visit */ }
  return kit
}
// The salon record's email branding, as a brand kit. The default charcoal is
// "not set", so it doesn't override a look's own accent. Settings shows this
// image as a round avatar, but it's often a wide logo, so the shape is left
// for detectLogoShape to decide from the picture itself.
export const kitFromSalon = (salon) => {
  if (!salon) return null
  const accent = normHex(salon.emailPrimaryColor)
  // emailHeaderImageUrl is the header booking emails use: a stored full URL,
  // or the salon photo named by emailHeaderImage
  const logo = salon.emailHeaderImageUrl || salon.imageUrl || ''
  const usable = logo && !/salonpic/.test(logo)
  const kit = {
    accent: accent && accent !== '#29282D' ? accent : undefined,
    logo: usable ? logo : undefined,
    name: salon.salonName || undefined,
  }
  Object.keys(kit).forEach(k => kit[k] === undefined && delete kit[k])
  return kit
}
// What a brand kit contributes to an email's theme spec. The look comes from
// the template (a saved look is only a preference for looks marked 'auto').
export const KIT_KEYS = ['accent', 'logo', 'logoWidth', 'logoShape', 'logoShapeFor', 'name', 'display', 'text', 'button', 'radius']

// How to show a logo nobody has chosen a shape for yet. Wide or tall images
// are logos: shown as they are, never cropped. A square-ish photo (JPG, or a
// PNG with no see-through pixels) reads best as a circle. Anything we can't
// load or measure stays natural, which never cuts anything off.
export const detectLogoShape = (url) => new Promise((resolve) => {
  if (!url || typeof Image === 'undefined') { resolve('natural'); return }
  const squareish = (img) => {
    const r = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0
    return r >= 0.85 && r <= 1.18
  }
  // Without pixel access (the server sends no CORS headers) judge on the
  // shape and file type alone: PNGs are usually logos, JPGs photos.
  const byShape = () => {
    const plain = new Image()
    plain.onload = () => resolve(squareish(plain) && !/\.(png|svg|gif)(\?|$)/i.test(url) ? 'round' : 'natural')
    plain.onerror = () => resolve('natural')
    plain.src = url
  }
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.onload = () => {
    if (!squareish(img)) { resolve('natural'); return }
    try {
      const size = 48
      const c = document.createElement('canvas')
      c.width = size
      c.height = size
      const ctx = c.getContext('2d')
      ctx.drawImage(img, 0, 0, size, size)
      const px = ctx.getImageData(0, 0, size, size).data
      // Corners carry the answer: a logo on a see-through or plain white
      // background would lose its edges to a circle; a photo fills them.
      const blankAt = (x, y) => { const i = (y * size + x) * 4; return px[i + 3] < 200 || (px[i] > 245 && px[i + 1] > 245 && px[i + 2] > 245) }
      const blank = [blankAt(1, 1), blankAt(size - 2, 1), blankAt(1, size - 2), blankAt(size - 2, size - 2)].filter(Boolean).length
      resolve(blank >= 3 ? 'natural' : 'round')
    } catch (e) {
      byShape()
    }
  }
  img.onerror = byShape
  // Its own address, so a copy the browser cached without CORS headers
  // (from the <img> in the canvas) can't block reading the pixels
  img.src = /^https?:/i.test(url) ? `${url}${url.includes('?') ? '&' : '?'}beu-shape=1` : url
})
export const specWithKit = (spec, kit) => {
  const next = { ...(spec || {}) }
  if (kit) KIT_KEYS.forEach(k => { if (kit[k] !== undefined && kit[k] !== null && kit[k] !== '') next[k] = kit[k] })
  return next
}
// Business type (Settings > Business) to template market.
// A beauty business's specialism, for the words only (designs and templates
// stay by market): nails, lashes and brows, or skin. A general beauty salon
// has none and gets words that suit any beauty business.
export const specialismFor = (businessType) => {
  if (businessType === 'nail_salon') return 'nails'
  if (businessType === 'lash_brow') return 'lashes'
  if (['spa', 'aesthetics'].includes(businessType)) return 'skin'
  return null
}

export const marketFor = (businessType) => {
  if (businessType === 'barber') return 'barber'
  if (['beauty_salon', 'nail_salon', 'spa', 'aesthetics', 'lash_brow', 'beauty'].includes(businessType)) return 'beauty'
  if (businessType === 'hair_salon' || businessType === 'hair') return 'hair'
  return null
}

// The theme spec an email carries lives on its footer block (every email has
// exactly one footer and it can't be deleted), so the look travels with
// drafts, My Templates and campaigns without a schema change.
export const themeSpecFromBlocks = (blocks) => {
  const footer = (blocks || []).find(b => b && b.type === 'footer')
  return (footer && footer.content && footer.content.theme) || null
}
export const withThemeSpec = (blocks, spec) => (blocks || []).map(b => (
  b && b.type === 'footer' ? { ...b, content: { ...b.content, style: 'v2', theme: spec } } : b
))
